/**
 * FormattedText.tsx
 *
 * Lightweight, safe renderer for the small subset of markdown that our
 * AI-generated summaries (patient / lead / plan / appointment) emit. We do
 * NOT use `dangerouslySetInnerHTML`; everything is expressed as React nodes
 * so XSS is not possible.
 *
 * Supported syntax (intentionally narrow):
 *   - Paragraph breaks on blank lines
 *   - Soft line breaks on single newlines
 *   - Bullet lists introduced by `-`, `*`, or `•`
 *   - Inline `**bold**`, `*italic*`, and `` `code` ``
 *
 * Anything else is passed through verbatim, which is exactly the behavior
 * we want for free-form LLM output.
 */

import { Fragment, type ReactNode } from 'react';

interface FormattedTextProps {
  text: string | null | undefined;
  className?: string;
}

const INLINE_REGEX = /(\*\*([^*]+)\*\*)|(\*([^*\n]+)\*)|(`([^`\n]+)`)/;

function parseInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    const match = remaining.match(INLINE_REGEX);
    if (!match || match.index === undefined) {
      out.push(remaining);
      break;
    }
    if (match.index > 0) {
      out.push(remaining.slice(0, match.index));
    }
    if (match[2] !== undefined) {
      out.push(
        <strong key={`b-${key++}`} className="font-semibold">
          {match[2]}
        </strong>
      );
    } else if (match[4] !== undefined) {
      out.push(
        <em key={`i-${key++}`} className="italic">
          {match[4]}
        </em>
      );
    } else if (match[6] !== undefined) {
      out.push(
        <code
          key={`c-${key++}`}
          className="rounded bg-black/10 px-1 py-0.5 text-[0.85em] font-mono"
        >
          {match[6]}
        </code>
      );
    }
    remaining = remaining.slice(match.index + match[0].length);
  }
  return out;
}

export function FormattedText({ text, className }: FormattedTextProps) {
  if (!text) return null;

  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let paragraph: string[] = [];
  let bullets: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const key = `p-${blocks.length}`;
    blocks.push(
      <p key={key}>
        {paragraph.map((line, i) => (
          <Fragment key={i}>
            {i > 0 ? <br /> : null}
            {parseInline(line)}
          </Fragment>
        ))}
      </p>
    );
    paragraph = [];
  };

  const flushBullets = () => {
    if (bullets.length === 0) return;
    const key = `ul-${blocks.length}`;
    blocks.push(
      <ul key={key} className="list-disc space-y-0.5 pl-5">
        {bullets.map((item, i) => (
          <li key={i}>{parseInline(item)}</li>
        ))}
      </ul>
    );
    bullets = [];
  };

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();
    if (!trimmed) {
      flushParagraph();
      flushBullets();
      continue;
    }
    const bulletMatch = trimmed.match(/^[-*\u2022]\s+(.*)$/);
    if (bulletMatch) {
      flushParagraph();
      bullets.push(bulletMatch[1]);
    } else {
      flushBullets();
      paragraph.push(trimmed);
    }
  }
  flushParagraph();
  flushBullets();

  return <div className={className ? `space-y-2 ${className}` : 'space-y-2'}>{blocks}</div>;
}
